<script lang="ts">
    import { onMount } from 'svelte';
    import { fly } from 'svelte/transition';
    import { TEXT_DIRECTION } from '@amp/web-app-components/src/constants';
    import { getUniqueIdGenerator } from '@amp/web-app-components/src/utils/uniqueId';

    const WIDTH = 300;
    const TRANSITION_DURATION = 300;
    let isRTL = false;

    export let show: boolean;
    export let isEmpty: boolean = false;
    // generate a random panelId to use for aria-owns property
    export const panelId = getUniqueIdGenerator()();

    $: transitionX = isRTL ? -1 * WIDTH : WIDTH;

    onMount(() => {
        isRTL = document.dir === TEXT_DIRECTION.RTL;
    });
</script>

{#if show}
    <div
        role="complementary"
        aria-labelledby={`${panelId}-panel-heading`}
        id={panelId}
        class="side-panel"
        data-testid="side-panel"
        transition:fly={{
            x: transitionX,
            duration: TRANSITION_DURATION,
            opacity: 1,
        }}
    >
        <!-- STICKY HEADING -->
        {#if $$slots['header']}
            <div class="side-panel-header-wrapper">
                <div class="side-panel-header">
                    <h3
                        id={`${panelId}-panel-heading`}
                        data-testid="side-panel-header"
                    >
                        <slot name="header" />
                    </h3>
                    <div
                        class="side-panel-controls"
                        data-testid="side-panel-controls"
                    >
                        <slot name="controls" />
                    </div>
                </div>

                {#if $$slots['subheader']}
                    <div
                        class="side-panel-subheader"
                        data-testid="side-panel-subheader"
                    >
                        <slot name="subheader" />
                    </div>
                {/if}
            </div>
        {/if}

        <!-- CONTENT -->
        <!-- Do not render content when it's set to empty -->
        {#if !isEmpty || !$$slots['empty']}
            <slot name="content" />
        {/if}

        <!-- EMPTY STATE -->
        {#if $$slots['empty'] && isEmpty}
            <div class="side-panel-empty" data-testid="side-panel-empty">
                <slot name="empty" />
            </div>
        {/if}
    </div>
{/if}

<style lang="scss">
    @use 'amp/stylekit/core/mixins/materials' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/keyline-border' as *;
    @use '@amp/web-shared-styles/app/core/mixins/absolute-center' as *;

    $web-chrome-drawer-width: 300px;
    $side-panel-header-horizontal-padding: 20px;
    $side-panel-header-height: 58px;

    .side-panel {
        --side-panel-horizontal-padding: #{$side-panel-header-horizontal-padding};

        @media (--small) {
            width: $web-chrome-drawer-width;
            height: calc(100vh - #{$global-player-bar-height});
            position: fixed;
            top: $global-player-bar-height;
            bottom: 0;
            border-top: 1px solid var(--playerDropShadow2); // Blend in top of drawer to bottom shadow of player
            box-shadow: 0 0 3px rgba(0, 0, 0, 0.1);
            overflow-x: hidden;
            overflow-y: auto;

            // When container is scrolling a focused item into view,
            // keep it under the sticky header
            scroll-padding-top: $side-panel-header-height;
            animation-duration: 0.3s;
            animation-fill-mode: forwards;
            border-inline-start: $keyline-border-width solid
                var(--systemQuaternary);
            inset-inline-end: 0;
            z-index: calc(var(--z-web-chrome) + 1);

            @include system-toolbar-titlebar-material($detect-inactive: true);
        }

        .side-panel-header-wrapper {
            padding-top: 23px;
            padding-bottom: 12px;
            position: sticky;
            top: 0;
            display: flex;
            flex-direction: column;

            // prettier-ignore
            padding-inline-start: $side-panel-header-horizontal-padding;
            padding-inline-end: $side-panel-header-horizontal-padding;
            z-index: calc(var(--z-default) + 6);

            @include system-toolbar-titlebar-material($detect-inactive: true);

            .side-panel-header {
                display: flex;
                justify-content: space-between;
                color: var(--systemPrimary);

                h3 {
                    padding-inline-end: 10px;
                    font: var(--title-2-emphasized);
                }
            }
        }

        .side-panel-empty {
            height: 100%;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            text-align: center;
            font: var(--callout);

            @include absolute-center;

            @media (--small) {
                position: absolute;
                z-index: calc(var(--z-default) + 4);
            }
        }

        .side-panel-controls {
            display: flex;
            align-items: center;
        }
    }
</style>
